import React, { createContext, useContext, useReducer } from 'react'

// Estado inicial
const initialState = {
  // Configurações gerais
  siteConfig: {
    name: import.meta.env.VITE_APP_NAME || 'Temporiza',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: import.meta.env.VITE_APP_ENV || 'development',
  },
  
  // Estado da UI
  ui: {
    loading: false,
    menuOpen: false,
    modalOpen: null,
    theme: 'light',
  },
  
  // Estado dos formulários
  forms: {
    contact: { loading: false, success: false, error: null },
    demo: { loading: false, success: false, error: null },
    newsletter: { loading: false, success: false, error: null },
  },
  
  // Analytics e tracking
  analytics: {
    enabled: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    gaId: import.meta.env.VITE_GA_MEASUREMENT_ID,
    gtmId: import.meta.env.VITE_GTM_ID,
  },
}

// Tipos de ações
export const ACTIONS = {
  // UI Actions
  SET_LOADING: 'SET_LOADING',
  TOGGLE_MENU: 'TOGGLE_MENU',
  SET_MODAL: 'SET_MODAL',
  SET_THEME: 'SET_THEME',
  
  // Form Actions
  FORM_SUBMIT_START: 'FORM_SUBMIT_START',
  FORM_SUBMIT_SUCCESS: 'FORM_SUBMIT_SUCCESS',
  FORM_SUBMIT_ERROR: 'FORM_SUBMIT_ERROR',
  FORM_RESET: 'FORM_RESET',
}

// Reducer
const institutionalReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        ui: { ...state.ui, loading: action.payload }
      }
    
    case ACTIONS.TOGGLE_MENU:
      return {
        ...state,
        ui: { ...state.ui, menuOpen: !state.ui.menuOpen }
      }
    
    case ACTIONS.SET_MODAL:
      return {
        ...state,
        ui: { ...state.ui, modalOpen: action.payload }
      }
    
    case ACTIONS.SET_THEME:
      return {
        ...state,
        ui: { ...state.ui, theme: action.payload }
      }
    
    case ACTIONS.FORM_SUBMIT_START:
      return {
        ...state,
        forms: {
          ...state.forms,
          [action.payload.formType]: {
            loading: true,
            success: false,
            error: null
          }
        }
      }
    
    case ACTIONS.FORM_SUBMIT_SUCCESS:
      return {
        ...state,
        forms: {
          ...state.forms,
          [action.payload.formType]: {
            loading: false,
            success: true,
            error: null
          }
        }
      }
    
    case ACTIONS.FORM_SUBMIT_ERROR:
      return {
        ...state,
        forms: {
          ...state.forms,
          [action.payload.formType]: {
            loading: false,
            success: false,
            error: action.payload.error
          }
        }
      }
    
    case ACTIONS.FORM_RESET:
      return {
        ...state,
        forms: {
          ...state.forms,
          [action.payload.formType]: {
            loading: false,
            success: false,
            error: null
          }
        }
      }
    
    default:
      return state
  }
}

// Context
const InstitutionalContext = createContext()

// Provider
export const InstitutionalProvider = ({ children }) => {
  const [state, dispatch] = useReducer(institutionalReducer, initialState)

  // Actions
  const actions = {
    setLoading: (loading) => 
      dispatch({ type: ACTIONS.SET_LOADING, payload: loading }),
    
    toggleMenu: () => 
      dispatch({ type: ACTIONS.TOGGLE_MENU }),
    
    setModal: (modalType) => 
      dispatch({ type: ACTIONS.SET_MODAL, payload: modalType }),
    
    setTheme: (theme) => 
      dispatch({ type: ACTIONS.SET_THEME, payload: theme }),
    
    formSubmitStart: (formType) => 
      dispatch({ type: ACTIONS.FORM_SUBMIT_START, payload: { formType } }),
    
    formSubmitSuccess: (formType) => 
      dispatch({ type: ACTIONS.FORM_SUBMIT_SUCCESS, payload: { formType } }),
    
    formSubmitError: (formType, error) => 
      dispatch({ type: ACTIONS.FORM_SUBMIT_ERROR, payload: { formType, error } }),
    
    formReset: (formType) => 
      dispatch({ type: ACTIONS.FORM_RESET, payload: { formType } }),
  }

  const value = {
    state,
    actions,
    // Computed values
    isLoading: state.ui.loading,
    isMenuOpen: state.ui.menuOpen,
    currentModal: state.ui.modalOpen,
    currentTheme: state.ui.theme,
  }

  return (
    <InstitutionalContext.Provider value={value}>
      {children}
    </InstitutionalContext.Provider>
  )
}

// Hook personalizado
export const useInstitutional = () => {
  const context = useContext(InstitutionalContext)
  
  if (!context) {
    throw new Error('useInstitutional deve ser usado dentro de InstitutionalProvider')
  }
  
  return context
}

export default InstitutionalContext
